/**
 * Social Operating Spine — code-owned defaults (§23.8).
 *
 * "Initial defaults live in one server-safe module and are overridden only
 * with a recorded reason." This is that module. It has no React, DOM, or
 * Supabase imports so it is safe to import from a Deno edge function.
 *
 * Lease/backoff numbers are implementation defaults subject to load/fault
 * test tuning before cutover (§23.8) — they are deliberately owned here
 * rather than scattered as magic numbers across the dispatcher.
 */

// ---------------------------------------------------------------------------
// Attempt lease
// ---------------------------------------------------------------------------

/** 2 minutes. */
export const ATTEMPT_LEASE_DURATION_MS = 2 * 60 * 1000;
/** Renew at 50% of the lease duration. */
export const ATTEMPT_LEASE_RENEW_AT_RATIO = 0.5;
export const ATTEMPT_LEASE_RENEW_AFTER_MS =
  ATTEMPT_LEASE_DURATION_MS * ATTEMPT_LEASE_RENEW_AT_RATIO;

// ---------------------------------------------------------------------------
// Claim + retry policy
// ---------------------------------------------------------------------------

/** Matches the current conservative scheduler scale. */
export const CLAIM_BATCH_SIZE = 5;

/** Maximum automatic attempts, unless provider policy explicitly lowers it. */
export const MAX_AUTOMATIC_ATTEMPTS = 3;

export interface BackoffPolicy {
  readonly kind: 'exponential_jitter';
  readonly baseMs: number;
  readonly maxMs: number;
  /** Fraction of the computed delay randomized, e.g. 0.2 = ±20%. */
  readonly jitterRatio: number;
}

/** Exponential with jitter; a provider-declared `retryAfterMs` always wins over this. */
export const BACKOFF_POLICY: BackoffPolicy = {
  kind: 'exponential_jitter',
  baseMs: 1000,
  maxMs: 5 * 60 * 1000,
  jitterRatio: 0.2,
};

/**
 * Computes the backoff delay for a given (1-indexed) attempt number.
 * `randomFn` defaults to `Math.random` but accepts an injected deterministic
 * source so tests can assert exact values without stubbing the global.
 */
export function computeBackoffDelayMs(
  attemptNumber: number,
  policy: BackoffPolicy = BACKOFF_POLICY,
  randomFn: () => number = Math.random,
): number {
  const exponent = Math.max(0, attemptNumber - 1);
  const raw = Math.min(policy.maxMs, policy.baseMs * 2 ** exponent);
  const jitterSpan = raw * policy.jitterRatio;
  // randomFn() in [0,1) -> jitter in [-jitterSpan/2, +jitterSpan/2)
  const jitter = (randomFn() - 0.5) * jitterSpan;
  return Math.max(0, Math.round(raw + jitter));
}

/**
 * A provider-declared retry-after always wins over the computed backoff
 * (§23.8 "backoff" row).
 */
export function resolveRetryDelayMs(
  attemptNumber: number,
  providerRetryAfterMs: number | null | undefined,
  policy: BackoffPolicy = BACKOFF_POLICY,
  randomFn: () => number = Math.random,
): number {
  if (typeof providerRetryAfterMs === 'number' && providerRetryAfterMs >= 0) {
    return providerRetryAfterMs;
  }
  return computeBackoffDelayMs(attemptNumber, policy, randomFn);
}

// ---------------------------------------------------------------------------
// Ambiguity handling
// ---------------------------------------------------------------------------

/** Verify immediately when a provider lookup exists for the ambiguous result. */
export const AMBIGUITY_VERIFY_IMMEDIATELY = true;
/** Escalate to Attention if still unresolved after 15 minutes. */
export const AMBIGUITY_ESCALATE_AFTER_MS = 15 * 60 * 1000;

// ---------------------------------------------------------------------------
// Approval policy
// ---------------------------------------------------------------------------

/** Not required for the Consumer launch fixture. */
export const APPROVAL_REQUIRED_DEFAULT = false;
/** One-step when a governed (Franchise/Enterprise) workspace opts in. */
export const APPROVAL_STEPS_WHEN_GOVERNED = 1;

// ---------------------------------------------------------------------------
// Feed
// ---------------------------------------------------------------------------

export const FEED_PAGE_SIZE = 30;
export const FEED_PAGE_CURSOR_STYLE = 'cursor' as const;

/**
 * PROVISIONAL — "exact TTL/retention calibrated before rollout, not
 * user-configurable in v1" (§23.8). This value is a safe starting point for
 * the deterministic test harness, not a promoted operational constant.
 */
export const FEED_CACHE_TTL_MS = 5 * 60 * 1000;

// ---------------------------------------------------------------------------
// Feature flags — off by default outside named cohorts (§23.10)
// ---------------------------------------------------------------------------

export type SocialSpineFlag =
  | 'SOCIAL_SPINE_TARGET_WRITES'
  | 'SOCIAL_SPINE_DISPATCH'
  | 'SOCIAL_SPINE_FEED'
  | 'SOCIAL_SPINE_VERIFICATION_ONLY';

export const SOCIAL_SPINE_FLAG_DEFAULTS: Readonly<Record<SocialSpineFlag, boolean>> = {
  SOCIAL_SPINE_TARGET_WRITES: false,
  SOCIAL_SPINE_DISPATCH: false,
  SOCIAL_SPINE_FEED: true,
  SOCIAL_SPINE_VERIFICATION_ONLY: false,
};
